/**
 * Curated items for the public, no-signup demo at /try.
 *
 * These are REAL, cross-vendor-audited items pulled verbatim from the
 * production corpus (item ids preserved), so the demo shows the actual product,
 * not a marketing mock. The only authored fields are editorial: `skillLabel`
 * (a plain-English trap name) and `correctNote` (a one-line teach on why the
 * keyed answer is right). The passage / prompt / options / trap rationales are
 * the audited content. tr-666's passage drops a leaked "Etymology" section
 * header for readability; nothing else is altered.
 *
 * Five distinct trap types, ordered for rhythm: warm up on a quick word-choice,
 * then a modifier, then the wrong-question math trap (the big "aha"), then two
 * reading traps. Text may contain inline $math$ — render with MathText.
 */

export type DemoChoice = {
  id: string;
  text: string;
  correct: boolean;
  /** Why this wrong choice is bait. Empty on the correct choice. */
  trap: string;
};

export type DemoItem = {
  id: string;
  /** Internal module key; map to a user label via moduleLabel(). */
  module: "trapline" | "grammar" | "slice";
  /** Plain-English name of the trap this item teaches. */
  skillLabel: string;
  /** Stimulus. May contain a `_____` cloze blank or inline $math$. */
  passage: string;
  prompt: string;
  choices: DemoChoice[];
  correctId: string;
  /** Authored one-liner shown on reveal: why the keyed answer is right. */
  correctNote: string;
};

export const DEMO_ITEMS: DemoItem[] = [
  {
    id: "tr-2955",
    module: "trapline",
    skillLabel: "Words in context",
    passage:
      "The historian's account is admirably _____; she draws on letters, court records, and newspaper archives that previous scholars had overlooked.",
    prompt: "Which word best completes the text?",
    correctId: "D",
    correctNote:
      "The clue is the list of overlooked sources she draws on. That signals breadth, which is thorough. Each wrong choice twists one detail of the sentence.",
    choices: [
      { id: "A", text: "controversial", correct: false, trap: "Nothing indicates the account provoked disagreement." },
      { id: "B", text: "concise", correct: false, trap: "Drawing on many overlooked sources signals breadth, not brevity." },
      { id: "C", text: "speculative", correct: false, trap: "Reliance on documentary evidence is the opposite of speculation." },
      { id: "D", text: "thorough", correct: true, trap: "" },
    ],
  },
  {
    id: "gr-002",
    module: "grammar",
    skillLabel: "Misplaced modifier",
    passage: "Having submitted my application, the website confirmed receipt within seconds.",
    prompt: "Which revision corrects the modifier issue?",
    correctId: "B",
    correctNote:
      "The opening phrase has to describe whoever did the submitting. Only B puts 'I' right after it. The website cannot submit your application.",
    choices: [
      {
        id: "A",
        text: "Having submitted my application, the website confirmed receipt within seconds.",
        correct: false,
        trap: "The website is incorrectly presented as the entity that submitted the application.",
      },
      {
        id: "B",
        text: "Having submitted my application, I received confirmation from the website within seconds.",
        correct: true,
        trap: "",
      },
      {
        id: "C",
        text: "The website having confirmed receipt within seconds, my application was submitted.",
        correct: false,
        trap: "It reverses the likely sequence so confirmation appears to precede submission.",
      },
      {
        id: "D",
        text: "Having received my application, the website confirmed it within seconds.",
        correct: false,
        trap: "It shifts the action so the website is the actor that received the application.",
      },
    ],
  },
  {
    id: "sl-1103",
    module: "slice",
    skillLabel: "The wrong-question trap",
    passage: "Maria is 3 times as old as Tom. In 5 years, Maria will be twice as old as Tom.",
    prompt: "How old is Tom now?",
    correctId: "A",
    correctNote:
      "Tom is 5. The bait is 15, which is Maria's age now. Right math, wrong question. The digital SAT plants this trap constantly.",
    choices: [
      { id: "A", text: "5", correct: true, trap: "" },
      { id: "B", text: "15", correct: false, trap: "This is Maria's current age, not Tom's. The question asks for Tom." },
      { id: "C", text: "10", correct: false, trap: "This comes from solving the age equation incorrectly." },
      { id: "D", text: "2", correct: false, trap: "This sets up the age relationship the wrong way around." },
    ],
  },
  {
    id: "tr-666",
    module: "trapline",
    skillLabel: "Tone trap",
    passage:
      "The name Amazon is said to arise from a war Francisco de Orellana fought with the Tapuyas and other tribes. The women of the tribe fought alongside the men, as was their custom. Orellana derived the name Amazonas from the Amazons of Greek mythology, described by Herodotus and Diodorus.",
    prompt: "According to the passage, why did Orellana choose the name Amazon?",
    correctId: "A",
    correctNote:
      "The passage gives a plain historical reason. Each wrong choice slips in a tone the text never takes: mocking, heroic, or accusing.",
    choices: [
      {
        id: "A",
        text: "It likely originates from an explorer's encounter with indigenous tribes where women participated in combat.",
        correct: true,
        trap: "",
      },
      {
        id: "B",
        text: "The name reflects a trivial and misguided attempt by Orellana to romanticize a standard military conflict.",
        correct: false,
        trap: "This uses judgmental language that mischaracterizes the passage's neutral historical tone.",
      },
      {
        id: "C",
        text: "The name serves as a glorious tribute to the legendary bravery displayed by women during Orellana's conquest.",
        correct: false,
        trap: "This employs an overly heroic tone that is not present in the passage's matter-of-fact description.",
      },
      {
        id: "D",
        text: "The name was a calculated fabrication intended by Orellana to deceive Greek scholars about his findings.",
        correct: false,
        trap: "This introduces a suspicious, accusatory tone that contradicts the straightforward account.",
      },
    ],
  },
  {
    id: "tr-678",
    module: "trapline",
    skillLabel: "Overclaim",
    passage:
      "Public Health England reported that the total number of antibiotic resistant infections in England rose by 9% from 55,812 in 2017 to 60,788 in 2018, but antibiotic consumption had fallen by 9% from 20.0 to 18.2 defined daily doses per 1,000 inhabitants per day between 2014 and 2018.",
    prompt: "Which statement about the reported trends is best supported?",
    correctId: "C",
    correctNote:
      "Two real trends over different time spans. C states exactly that. The others invent a cause, a ranking, or a 2030 prediction the passage never makes.",
    choices: [
      {
        id: "A",
        text: "The statistics show a consistent and predictable correlation between lower antibiotic consumption and higher infection rates.",
        correct: false,
        trap: "The passage reports two trends in different timeframes but never calls their relationship consistent or predictable.",
      },
      {
        id: "B",
        text: "The 9% rise in resistant infections indicates that antibiotic resistance has surpassed other significant health concerns in England.",
        correct: false,
        trap: "The text gives infection growth data but makes no comparison to other health challenges.",
      },
      {
        id: "C",
        text: "Drug-resistant illnesses increased over a one-year period despite a notable reduction in antibiotic usage over a multi-year span.",
        correct: true,
        trap: "",
      },
      {
        id: "D",
        text: "Public Health England concluded that current trends will make resistant infections the leading cause of death by 2030.",
        correct: false,
        trap: "The passage gives retrospective statistics but draws no forward-looking conclusion about mortality.",
      },
    ],
  },
];
